/**
 * Employee Formatters
 * Utility functions for formatting employee data
 */

import { Employee, EmployeeStatus, EmployeeGender, MaritalStatus } from '../types/employee';
import { EMPLOYEE_STATUS_LABELS, GENDER_LABELS, MARITAL_STATUS_LABELS } from '../types/employee';

/**
 * Format CPF progressively as user types (000.000.000-00)
 */
export const formatCPF = (cpf: string): string => {
  if (!cpf) return '';
  const cleaned = cpf.replace(/\D/g, '').slice(0, 11);
  if (cleaned.length <= 3) return cleaned;
  if (cleaned.length <= 6) return `${cleaned.slice(0, 3)}.${cleaned.slice(3)}`;
  if (cleaned.length <= 9) return `${cleaned.slice(0, 3)}.${cleaned.slice(3, 6)}.${cleaned.slice(6)}`;
  return `${cleaned.slice(0, 3)}.${cleaned.slice(3, 6)}.${cleaned.slice(6, 9)}-${cleaned.slice(9)}`;
};

/**
 * Remove CPF formatting
 */
export const unformatCPF = (cpf: string): string => {
  return cpf.replace(/\D/g, '');
};

/**
 * Format phone number progressively as user types (XX) XXXXX-XXXX or (XX) XXXX-XXXX
 */
export const formatPhone = (phone: string): string => {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '').slice(0, 11);
  if (cleaned.length <= 2) return `(${cleaned}`;
  if (cleaned.length <= 6) return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2)}`;
  if (cleaned.length <= 10) return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`;
  // 11 dígitos — celular
  return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
};

/**
 * Remove phone formatting
 */
export const unformatPhone = (phone: string): string => {
  return phone.replace(/\D/g, '');
};

/**
 * Format CEP (00000-000)
 */
export const formatCEP = (cep: string): string => {
  if (!cep) return '';
  const cleaned = cep.replace(/\D/g, '');
  if (cleaned.length !== 8) return cep;
  return cleaned.replace(/(\d{5})(\d{3})/, '$1-$2');
};

/**
 * Remove CEP formatting
 */
export const unformatCEP = (cep: string): string => {
  return cep.replace(/\D/g, '');
};

/**
 * Format PIS/PASEP progressively as user types (000.00000.00-0)
 */
export const formatPIS = (pis: string): string => {
  if (!pis) return '';
  const cleaned = pis.replace(/\D/g, '').slice(0, 11);
  if (cleaned.length <= 3) return cleaned;
  if (cleaned.length <= 8) return `${cleaned.slice(0, 3)}.${cleaned.slice(3)}`;
  if (cleaned.length <= 10) return `${cleaned.slice(0, 3)}.${cleaned.slice(3, 8)}.${cleaned.slice(8)}`;
  return `${cleaned.slice(0, 3)}.${cleaned.slice(3, 8)}.${cleaned.slice(8, 10)}-${cleaned.slice(10)}`;
};

/**
 * Format CTPS progressively as user types (0000000 — 7 digits only)
 */
export const formatCTPS = (ctps: string): string => {
  if (!ctps) return '';
  return ctps.replace(/\D/g, '').slice(0, 7);
};

/**
 * Format date to Brazilian format (DD/MM/YYYY)
 */
export const formatDate = (date: string | Date | null): string => {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(dateObj.getTime())) return '';
  
  const day = dateObj.getDate().toString().padStart(2, '0');
  const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
  const year = dateObj.getFullYear();
  
  return `${day}/${month}/${year}`;
};

/**
 * Format date to ISO (YYYY-MM-DD)
 */
export const formatDateISO = (date: string | Date | null): string => {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(dateObj.getTime())) return '';
  
  return dateObj.toISOString().split('T')[0];
};

/**
 * Calculate age from birth date
 */
export const calculateAge = (birthDate: string | Date): number => {
  if (!birthDate) return 0;
  
  const birth = typeof birthDate === 'string' ? new Date(birthDate) : birthDate;
  const today = new Date();
  
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  
  return age;
};

/**
 * Format full name (capitalize each word)
 */
export const formatName = (name: string): string => {
  if (!name) return '';
  
  const exceptions = ['de', 'da', 'do', 'dos', 'das', 'e'];
  
  return name
    .toLowerCase()
    .split(' ')
    .map(word => {
      if (exceptions.includes(word)) {
        return word;
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
};

/**
 * Format employee status
 */
export const formatStatus = (status: EmployeeStatus): string => {
  return EMPLOYEE_STATUS_LABELS[status] || status;
};

/**
 * Format gender
 */
export const formatGender = (gender: EmployeeGender): string => {
  return GENDER_LABELS[gender] || gender;
};

/**
 * Format marital status
 */
export const formatMaritalStatus = (status: MaritalStatus): string => {
  return MARITAL_STATUS_LABELS[status] || status;
};

/**
 * Format full address
 */
export const formatAddress = (employee: Employee): string => {
  const parts: string[] = [];
  
  if (employee.address) parts.push(employee.address);
  if (employee.addressNumber) parts.push(`nº ${employee.addressNumber}`);
  if (employee.neighborhood) parts.push(employee.neighborhood);
  if (employee.city && employee.state) parts.push(`${employee.city}/${employee.state}`);
  if (employee.zipCode) parts.push(`CEP ${formatCEP(employee.zipCode)}`);
  
  return parts.join(', ');
};

/**
 * Format full name with CPF
 */
export const formatEmployeeIdentification = (employee: Employee): string => {
  return `${employee.name} (CPF: ${formatCPF(employee.cpf)})`;
};

/**
 * Get employee initials
 */
export const getInitials = (name: string): string => {
  if (!name) return '';
  
  const parts = name.trim().split(' ');
  
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/**
 * Format salary (BRL)
 */
export const formatSalary = (value: number | string): string => {
  if (!value) return 'R$ 0,00';
  
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(numValue);
};

/**
 * Format percentage
 */
export const formatPercentage = (value: number, decimals = 2): string => {
  if (value === null || value === undefined) return '0%';
  return `${value.toFixed(decimals)}%`;
};

/**
 * Check if employee is minor (< 18 years)
 */
export const isMinor = (birthDate: string | Date): boolean => {
  return calculateAge(birthDate) < 18;
};

/**
 * Check if employee is senior (>= 60 years)
 */
export const isSenior = (birthDate: string | Date): boolean => {
  return calculateAge(birthDate) >= 60;
};

/**
 * Get age range label
 */
export const getAgeRangeLabel = (birthDate: string | Date): string => {
  const age = calculateAge(birthDate);
  
  if (age < 18) return 'Menor de 18 anos';
  if (age < 30) return '18-29 anos';
  if (age < 40) return '30-39 anos';
  if (age < 50) return '40-49 anos';
  if (age < 60) return '50-59 anos';
  return '60+ anos';
};

/**
 * Format time in service
 */
export const formatTimeInService = (admissionDate: string | Date): string => {
  if (!admissionDate) return '';
  
  const admission = typeof admissionDate === 'string' ? new Date(admissionDate) : admissionDate;
  const today = new Date();
  
  const years = today.getFullYear() - admission.getFullYear();
  const months = today.getMonth() - admission.getMonth();
  
  const totalMonths = years * 12 + months;
  
  if (totalMonths < 0) return '';
  
  const displayYears = Math.floor(totalMonths / 12);
  const displayMonths = totalMonths % 12;
  
  const parts: string[] = [];
  
  if (displayYears > 0) {
    parts.push(`${displayYears} ano${displayYears > 1 ? 's' : ''}`);
  }
  
  if (displayMonths > 0) {
    parts.push(`${displayMonths} ${displayMonths > 1 ? 'meses' : 'mês'}`);
  }
  
  return parts.length > 0 ? parts.join(' e ') : 'Menos de 1 mês';
};
